// Hygeon hero — forked from Horizon's slideshow-component (assets/slideshow.js)
// in structure and convention (Component base class, ref system, on:click
// delegation, autoplay/visibilitychange pattern) but NOT in mechanism: the
// source site's hero uses Slider Revolution's opacity crossfade (per-slide
// duration, not uniform — see docs/hero-animation-spec.md §2), while
// Horizon's native slideshow is a horizontal scroll-snap carousel with no
// fade option (verified by grep — zero matches for "fade" anywhere in
// Horizon's slideshow code). Scroll-position-based selection and
// crossfade are fundamentally different mechanics, so select()/next()/
// previous()/the drag handler are rewritten here rather than reused.
// Deliberately does NOT pause on hover, unlike Horizon's native slideshow
// — the source's own config marks hover-pause explicitly off (docs/hero-
// animation-spec.md §6). An earlier version of this file paused on hover
// to match Horizon's convention rather than the source; corrected.
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

export class HygeonHero extends Component {
  requiredRefs = ['slides'];

  connectedCallback() {
    super.connectedCallback();

    this.current = 0;
    this.#showSlide(0, { instant: true });
    this.#setupKeyboard();
    this.#setupSwipe();

    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotionQuery.addEventListener('change', this.#handleReducedMotionChange);

    if (this.slideCount > 1) {
      document.addEventListener('visibilitychange', this.#handleVisibilityChange);
      this.resume();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotionQuery?.removeEventListener('change', this.#handleReducedMotionChange);
  }

  get slideCount() {
    return this.refs.slides?.length ?? 0;
  }

  /**
   * Selects a slide by index, wrapping around at either end.
   * @param {number|string} input
   */
  select(input) {
    const index = typeof input === 'string' ? parseInt(input, 10) : input;
    if (Number.isNaN(index) || !this.slideCount) return;

    const wrapped = ((index % this.slideCount) + this.slideCount) % this.slideCount;
    if (wrapped === this.current) return;

    this.#showSlide(wrapped);
  }

  next() {
    this.select(this.current + 1);
  }

  previous() {
    this.select(this.current - 1);
  }

  /**
   * @param {number} index
   * @param {Object} [options]
   * @param {boolean} [options.instant]
   */
  #showSlide(index, { instant = false } = {}) {
    const { slides, dots } = this.refs;
    if (!slides) return;

    const reduceMotion = prefersReducedMotion();

    slides.forEach((slide, i) => {
      const active = i === index;

      if (instant || reduceMotion) {
        slide.classList.add('hygeon-hero-slide--instant');
      }

      slide.classList.toggle('hygeon-hero-slide--active', active);
      slide.setAttribute('aria-hidden', String(!active));

      if (instant || reduceMotion) {
        // Force the browser to apply the instant state before removing it,
        // so the next real transition (not this one) animates.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => slide.classList.remove('hygeon-hero-slide--instant'));
        });
      }
    });

    dots?.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === index)));

    this.current = index;
  }

  /** Starts automatic slide playback. Does nothing under prefers-reduced-motion. */
  play() {
    if (this.#interval || prefersReducedMotion()) return;

    const interval = this.autoplayInterval;
    if (!interval) return;

    this.paused = false;
    this.setAttribute('aria-live', 'polite');

    this.#interval = setInterval(() => {
      // No pause-on-hover: the source site's own config marks hover-pause
      // explicitly off (settings.slideshow.hom/hfn both false, docs/hero-
      // animation-spec.md §6). Correcting an earlier build that paused on
      // hover to match Horizon's native slideshow convention instead of
      // the source — see CLAUDE.md's Architecture decisions.
      if (document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.#suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) this.setAttribute('paused', '');
    else this.removeAttribute('paused');
  }

  #suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  /** Autoplay interval in milliseconds — the `autoplay` attribute holds ms directly (unlike Horizon's native, which stores whole seconds). */
  get autoplayInterval() {
    if (prefersReducedMotion()) return undefined;

    const value = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(value) ? undefined : value;
  }

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {MediaQueryList|undefined} */
  #reducedMotionQuery;

  #handleVisibilityChange = () => (document.hidden ? this.#suspend() : this.resume());

  #handleReducedMotionChange = () => {
    if (prefersReducedMotion()) {
      this.#suspend();
    } else {
      this.resume();
    }
  };

  #setupKeyboard() {
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    });
  }

  /**
   * Simplified swipe-to-advance (not a continuous drag-follow like Horizon's
   * native slideshow, since there's no scroll position to drag here — a
   * threshold-based swipe fits a crossfade slideshow better than trying to
   * replicate scroll-drag physics on an opacity-only interface).
   */
  #setupSwipe() {
    const threshold = 40;
    /** @type {{x: number, y: number}|null} */
    let start = null;

    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      start = { x: event.clientX, y: event.clientY };
    });

    this.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;

      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;

      if (dx < 0) this.next();
      else this.previous();
    });
  }
}

if (!customElements.get('hygeon-hero')) {
  customElements.define('hygeon-hero', HygeonHero);
}
